import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrainIcon, MessageSquareIcon, ServerIcon, SaveIcon, CopyIcon, CheckIcon, RotateCcwIcon, ZapIcon, LoaderIcon, ChevronDownIcon, HelpCircleIcon, SearchIcon, Sparkles } from 'lucide-react';
import { useActiveBotConfiguration, useBotConfigurationOperations } from '../hooks/useBotConfiguration.js';
import { useQuery } from '@tanstack/react-query';
import { coreServicesApi, type BotConfiguration, type ModelEndpoint, type OpenRouterProvider, type ProviderDefinition, type ProviderModel } from '../lib/api/core-services.js';
import { containsVariable } from './bot-configuration-utils.js';
import type { SupportedBotName, SupportedProvider } from '@discord-bot-platform/service-client';
import {
  Badge,
  Button,
  Card,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@randompedestrian07/ui';
import clsx from 'clsx';

interface FormData {
  systemPrompt: string;
  userPrompt: string;
  provider: SupportedProvider;
  model: string;
  modelProvider?: string;
  maxTokens: number | string;
  temperature: number;
  promptCacheTtl: 'off' | '5m' | '1h';
}

interface PromptType {
  id: string;
  name: string;
  requiresVision?: boolean;
}

interface VariableHint {
  name: string;
  description?: string;
}

interface BotConfigurationManagerProps {
  botName: SupportedBotName;
  promptTypes: PromptType[];
  defaultPrompts: Record<string, { systemPrompt: string; userPrompt: string }>;
  defaultProvider: SupportedProvider;
  defaultModel: string;
  variableHints?: Record<string, VariableHint[]>;
}

const formatModelPricing = (model: ProviderModel): string => {
  if (model.inputCostPer1M !== undefined && model.outputCostPer1M !== undefined) {
    const inputCost = Number(model.inputCostPer1M).toFixed(2);
    const outputCost = Number(model.outputCostPer1M).toFixed(2);
    return `$${inputCost}/$${outputCost}`;
  } else if (model.costPer1MTokens !== undefined) {
    const cost = Number(model.costPer1MTokens).toFixed(2);
    return `$${cost}/1M`;
  }
  return '';
};

const modelSupportsVision = (model: ProviderModel): boolean => {
  if (model.supportsVision) return true;
  const caps = Array.isArray(model.capabilities) ? model.capabilities : [];
  return caps.includes('supportsVision') || caps.includes('vision');
};

const providerHasVisionModels = (provider: ProviderDefinition): boolean => {
  if (provider.models && provider.models.length > 0) {
    return provider.models.some(model => modelSupportsVision(model));
  }
  return Boolean(provider.capabilities?.supportsVision);
};

export const BotConfigurationManager: React.FC<BotConfigurationManagerProps> = ({
  botName,
  promptTypes,
  defaultPrompts,
  defaultProvider,
  defaultModel,
  variableHints
}) => {
  const [selectedPromptType, setSelectedPromptType] = useState(promptTypes[0]?.id || '');
  const [formData, setFormData] = useState<FormData>({
    systemPrompt: '',
    userPrompt: '',
    provider: defaultProvider,
    model: defaultModel,
    modelProvider: undefined,
    maxTokens: defaultProvider === 'nanogpt' ? 2000 : 200,
    temperature: defaultProvider === 'nanogpt' ? 1 : 0.8,
    promptCacheTtl: 'off'
  });

  const [saveStatus, setSaveStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isModelProviderDropdownOpen, setIsModelProviderDropdownOpen] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [modelProviderSearchTerm, setModelProviderSearchTerm] = useState('');
  const modelSearchInputRef = useRef<HTMLInputElement | null>(null);
  const modelProviderSearchInputRef = useRef<HTMLInputElement | null>(null);
  const systemPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const userPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const initializedPromptTypesRef = useRef<Set<string>>(new Set());

  const { data: currentConfig, configurations = [], isLoading: configurationsLoading, error: configurationsError } = useActiveBotConfiguration(botName, selectedPromptType);
  const { saveConfiguration, isSaving, error: saveError } = useBotConfigurationOperations(botName);

  const { data: providerModels, isLoading: modelsLoading, error: modelsError, refetch: refetchModels } = useQuery({
    queryKey: ['providerModels'],
    queryFn: () => coreServicesApi.llm.getProviderModels(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: openRouterProvidersData, isLoading: providersLoading } = useQuery<{ providers: OpenRouterProvider[] }>({
    queryKey: ['openRouterProviders'],
    queryFn: () => coreServicesApi.llm.getOpenRouterProviders(),
    enabled: formData.provider === 'openrouter',
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: modelEndpointsData, isLoading: endpointsLoading } = useQuery<{ endpoints: ModelEndpoint[] }>({
    queryKey: ['modelEndpoints', formData.model],
    queryFn: () => coreServicesApi.llm.getModelEndpoints(formData.model),
    enabled: formData.provider === 'openrouter' && !!formData.model,
    staleTime: 1 * 60 * 1000,
  });

  const loading = configurationsLoading || isSaving;

  const providers = useMemo<ProviderDefinition[]>(() => {
    if (providerModels?.providers) {
      return Object.values(providerModels.providers);
    }
    return [
      { id: 'gemini', name: 'Google Gemini', models: [], capabilities: { supportsLewd: false, supportsVision: true, maxContextLength: 0 } },
      { id: 'deepseek', name: 'DeepSeek', models: [], capabilities: { supportsLewd: false, supportsVision: false, maxContextLength: 0 } },
      { id: 'openrouter', name: 'OpenRouter', models: [], capabilities: { supportsLewd: false, supportsVision: false, maxContextLength: 0 } },
      { id: 'nanogpt', name: 'NanoGPT', models: [], capabilities: { supportsLewd: false, supportsVision: false, maxContextLength: 0 } },
      { id: 'ollama', name: 'Ollama', models: [], capabilities: { supportsLewd: false, supportsVision: false, maxContextLength: 0 } },
      { id: 'anthropic', name: 'Anthropic', models: [], capabilities: { supportsLewd: false, supportsVision: true, maxContextLength: 200000 } }
    ];
  }, [providerModels]);

  const selectedPrompt = promptTypes.find((p) => p.id === selectedPromptType);
  const promptRequiresVision = Boolean(selectedPrompt?.requiresVision);

  const availableProviders = useMemo(() => {
    if (!promptRequiresVision) return providers;
    return providers.filter(p => providerHasVisionModels(p));
  }, [providers, promptRequiresVision]);

  const currentProvider = providers.find(provider => provider.id === formData.provider) || providers[0];
  const currentVariableHints = variableHints?.[selectedPromptType] ?? [];
  const variableNames = currentVariableHints.map(hint => hint.name);
  const systemPromptMatches = variableNames.filter(name => containsVariable(formData.systemPrompt, name));
  const userPromptMatches = variableNames.filter(name => containsVariable(formData.userPrompt, name));
  const matchedVariables = new Set([...systemPromptMatches, ...userPromptMatches]);
  const variableTooltip = currentVariableHints.length > 0
    ? ['Available placeholders:']
      .concat(
        currentVariableHints.map(hint => `{${hint.name}}${hint.description ? ` — ${hint.description}` : ''}`)
      )
      .join('\n')
    : 'No dynamic placeholders currently defined for this prompt type.';
  const matchesModelSearch = useCallback((model: ProviderModel) => {
    if (!modelSearchTerm.trim()) return true;
    const term = modelSearchTerm.trim().toLowerCase();
    const name = typeof model?.name === 'string' ? model.name.toLowerCase() : '';
    const id = typeof model?.id === 'string' ? model.id.toLowerCase() : '';
    const tags = Array.isArray(model?.tags) ? model.tags : [];
    const tagMatch = tags.some((tag) => typeof tag === 'string' && tag.toLowerCase().includes(term));
    return name.includes(term) || id.includes(term) || tagMatch;
  }, [modelSearchTerm]);
  const filteredModels = useMemo(() => {
    const models = (currentProvider?.models || []).filter(matchesModelSearch);
    if (!promptRequiresVision) return models;
    return models.filter(model => modelSupportsVision(model));
  }, [currentProvider, matchesModelSearch, promptRequiresVision]);
  const hasFilteredModels = filteredModels.length > 0;

  const allModelProviders = useMemo(() => {
    if (!openRouterProvidersData?.providers) return [];
    return openRouterProvidersData.providers;
  }, [openRouterProvidersData]);

  const availableModelProviders = useMemo(() => {
    if (!modelEndpointsData?.endpoints || modelEndpointsData.endpoints.length === 0) {
      return allModelProviders;
    }
    const supportedTags = new Set(modelEndpointsData.endpoints.map((ep) => ep.providerCode));
    return allModelProviders.filter((p) => supportedTags.has(p.code));
  }, [allModelProviders, modelEndpointsData]);

  const matchesProviderSearch = useCallback((provider: OpenRouterProvider) => {
    if (!modelProviderSearchTerm.trim()) return true;
    const term = modelProviderSearchTerm.trim().toLowerCase();
    const name = provider?.name?.toLowerCase() || '';
    const code = provider?.code?.toLowerCase() || '';
    return name.includes(term) || code.includes(term);
  }, [modelProviderSearchTerm]);

  const filteredModelProviders = useMemo(() => {
    return availableModelProviders.filter(matchesProviderSearch);
  }, [availableModelProviders, matchesProviderSearch]);

  const getProviderUptime = useCallback((providerCode: string) => {
    if (!modelEndpointsData?.endpoints) return null;
    const endpoint = modelEndpointsData.endpoints.find((ep) => ep.providerCode === providerCode);
    return endpoint?.uptime;
  }, [modelEndpointsData]);

  const isModelProviderEnabled = formData.provider === 'openrouter';
  const showSystemHighlight = systemPromptMatches.length > 0;
  const showUserHighlight = userPromptMatches.length > 0;
  useEffect(() => {
    if (isModelDropdownOpen) {
      requestAnimationFrame(() => {
        modelSearchInputRef.current?.focus();
      });
    } else {
      setModelSearchTerm('');
    }
  }, [isModelDropdownOpen]);

  useEffect(() => {
    setModelSearchTerm('');
  }, [currentProvider?.id]);

  useEffect(() => {
    if (formData.provider === 'openrouter' && formData.modelProvider && modelEndpointsData?.endpoints) {
      const supportedTags = new Set(modelEndpointsData.endpoints.map((ep) => ep.providerCode));
      if (!supportedTags.has(formData.modelProvider)) {
        setFormData(prev => ({ ...prev, modelProvider: undefined }));
      }
    }
  }, [formData.provider, formData.model, formData.modelProvider, modelEndpointsData]);

  useEffect(() => {
    if (formData.provider !== 'openrouter' && formData.modelProvider) {
      setFormData(prev => ({ ...prev, modelProvider: undefined }));
    }
  }, [formData.provider, formData.model]);

  useEffect(() => {
    if (isModelProviderDropdownOpen) {
      requestAnimationFrame(() => {
        modelProviderSearchInputRef.current?.focus();
      });
    } else {
      setModelProviderSearchTerm('');
    }
  }, [isModelProviderDropdownOpen]);

  useEffect(() => {
    if (configurations === undefined) return;

    // Fix: Prevent infinite update loops by checking if we already initialized this specific combination
    const initKey = `${botName}:${selectedPromptType}`;
    if (initializedPromptTypesRef.current.has(initKey)) return;

    const config = configurations?.find(c =>
      c.botName === botName &&
      c.promptType === selectedPromptType &&
      c.isActive
    );

    if (config) {
      const recommendedMaxTokens = config.provider === 'nanogpt' ? 2000 : 200;
      const recommendedTemperature = config.provider === 'nanogpt' ? 1 : 0.8;
      const maxTokensMeta = config.metadata?.maxTokens;
      const temperatureMeta = config.metadata?.temperature;
      const promptCacheTtlMeta = config.metadata?.promptCacheTtl;
      const promptCacheTtl = promptCacheTtlMeta === '5m' || promptCacheTtlMeta === '1h'
        ? promptCacheTtlMeta
        : 'off';

      setFormData({
        systemPrompt: config.systemPrompt || '',
        userPrompt: config.userPrompt || '',
        provider: config.provider,
        model: config.model,
        modelProvider: config.modelProvider,
        maxTokens: typeof maxTokensMeta === 'number' ? maxTokensMeta : recommendedMaxTokens,
        temperature: typeof temperatureMeta === 'number' ? temperatureMeta : recommendedTemperature,
        promptCacheTtl
      });
      initializedPromptTypesRef.current.add(initKey);
    } else {
      const defaults = defaultPrompts[selectedPromptType];
      const defaultMaxTokens = defaultProvider === 'nanogpt' ? 2000 : 200;
      const defaultTemperature = defaultProvider === 'nanogpt' ? 1 : 0.8;

      const newState = defaults ? {
        systemPrompt: defaults.systemPrompt,
        userPrompt: defaults.userPrompt,
        provider: defaultProvider,
        model: defaultModel,
        modelProvider: undefined,
        maxTokens: defaultMaxTokens,
        temperature: defaultTemperature,
        promptCacheTtl: 'off'
      } : {
        systemPrompt: '',
        userPrompt: '',
        provider: defaultProvider,
        model: defaultModel,
        modelProvider: undefined,
        maxTokens: defaultMaxTokens,
        temperature: defaultTemperature,
        promptCacheTtl: 'off'
      };

      setFormData(newState as FormData);
      initializedPromptTypesRef.current.add(initKey);
    }
  }, [selectedPromptType, configurations, botName, defaultProvider, defaultModel, defaultPrompts]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (isProviderDropdownOpen && !target?.closest('[data-dropdown="provider-selector"]')) {
        setIsProviderDropdownOpen(false);
      }

      if (isModelDropdownOpen && !target?.closest('[data-dropdown="model-selector"]')) {
        setIsModelDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isProviderDropdownOpen, isModelDropdownOpen]);

  useEffect(() => {
    if (saveError) {
      setSaveStatus('Failed to save configuration');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, [saveError]);

  useEffect(() => {
    if (configurationsError) {
      setSaveStatus('Failed to load configurations');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, [configurationsError]);

  const handleFormDataChange = useCallback((field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const insertVariablePlaceholder = useCallback((variableName: string) => {
    const placeholder = `{{${variableName}}}`;
    const activeElement = document.activeElement;
    let targetRef = userPromptRef;
    let field: keyof FormData = 'userPrompt';

    if (activeElement === systemPromptRef.current) {
      targetRef = systemPromptRef;
      field = 'systemPrompt';
    }

    const target = targetRef.current;
    if (!target) {
      const newValue = `${formData.userPrompt}${placeholder}`;
      handleFormDataChange('userPrompt', newValue);
      return;
    }

    const selectionStart = target.selectionStart ?? target.value.length;
    const selectionEnd = target.selectionEnd ?? target.value.length;
    const originalValue = target.value;
    const newValue = `${originalValue.slice(0, selectionStart)}${placeholder}${originalValue.slice(selectionEnd)}`;

    handleFormDataChange(field, newValue);

    requestAnimationFrame(() => {
      target.focus();
      const cursorPosition = selectionStart + placeholder.length;
      target.setSelectionRange(cursorPosition, cursorPosition);
    });
  }, [formData.userPrompt, handleFormDataChange]);

  const handleProviderChange = useCallback((providerId: FormData['provider']) => {
    const newProvider = providers.find(p => p.id === providerId);

    let firstModel = '';
    if (promptRequiresVision && newProvider?.models) {
      const visionModel = newProvider.models.find(m => modelSupportsVision(m));
      firstModel = visionModel?.id || '';
    } else {
      firstModel = newProvider?.models?.[0]?.id || '';
    }

    const fallbackModel = providerId === 'nanogpt' ? 'deepseek-ai/DeepSeek-V3.1-Terminus' : '';

    setFormData(prev => ({
      ...prev,
      provider: providerId,
      model: firstModel || fallbackModel || prev.model,
      modelProvider: undefined,
      ...(providerId === 'nanogpt'
        ? { maxTokens: 2000, temperature: 1 }
        : {})
    }));
    setIsProviderDropdownOpen(false);
    setIsModelDropdownOpen(false);
    setIsModelProviderDropdownOpen(false);
    setModelSearchTerm('');
    setModelProviderSearchTerm('');
  }, [providers, promptRequiresVision]);

  const handleSave = useCallback(async () => {
    try {
      setSaveStatus('Saving...');

      const promptData: Partial<BotConfiguration> = {
        botName,
        promptType: selectedPromptType,
        systemPrompt: formData.systemPrompt,
        userPrompt: formData.userPrompt,
        provider: formData.provider,
        model: formData.model,
        isActive: true,
        metadata: {
          maxTokens: typeof formData.maxTokens === 'string' ? (parseInt(formData.maxTokens) || 200) : formData.maxTokens,
          temperature: formData.temperature,
          ...(formData.promptCacheTtl !== 'off' ? { promptCacheTtl: formData.promptCacheTtl } : {})
        }
      };

      if (formData.provider === 'openrouter' && formData.modelProvider) {
        promptData.modelProvider = formData.modelProvider;
      }

      await saveConfiguration(promptData, currentConfig?.id);
      setSaveStatus('Saved!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  }, [botName, selectedPromptType, formData, saveConfiguration, currentConfig?.id]);

  const handleReset = useCallback(() => {
    const defaults = defaultPrompts[selectedPromptType];
    if (defaults) {
      setFormData(prev => ({
        ...prev,
        systemPrompt: defaults.systemPrompt,
        userPrompt: defaults.userPrompt
      }));
    }
  }, [selectedPromptType, defaultPrompts]);

  const handleCopy = useCallback(() => {
    const content = `System Prompt:\n${formData.systemPrompt}\n\nUser Prompt:\n${formData.userPrompt}`;
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [formData.systemPrompt, formData.userPrompt]);

  return (
    <div className="bg-panel-strong/40 border border-stroke p-6 rounded-3xl backdrop-blur-xl shadow-2xl">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-1/4 space-y-6">
          <div>
            <h2 className="text-[10px] font-bold uppercase tracking-widest text-muted/60 mb-4 px-1">Prompt Types</h2>
            <div className="space-y-1.5">
              {promptTypes.map(promptType => (
                <button
                  key={promptType.id}
                  className={clsx(
                    "w-full text-left p-3.5 rounded-2xl transition-all duration-200 group relative overflow-hidden border",
                    selectedPromptType === promptType.id
                      ? "bg-accent/10 border-accent/30 text-accent shadow-[0_0_20px_rgba(var(--accent-rgb),0.1)]"
                      : "border-transparent text-muted hover:bg-white/5 hover:text-text"
                  )}
                  onClick={() => setSelectedPromptType(promptType.id)}
                >
                  <div className="flex items-center justify-between relative z-10">
                    <span className="font-semibold text-sm">{promptType.name}</span>
                    {promptType.requiresVision && (
                      <Badge variant="secondary" className="bg-accent/20 text-accent border-accent/30 text-[9px] px-1.5 py-0">Vision</Badge>
                    )}
                  </div>
                  {selectedPromptType === promptType.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 bg-white/5 rounded-2xl border border-stroke space-y-3">
            <Button
              variant="ghost"
              className="w-full shadow-lg shadow-accent/20 h-11 text-sm font-bold !bg-accent !text-black hover:!bg-accent/90 !border-none"
              onClick={handleSave}
              disabled={loading || (!formData.systemPrompt && !formData.userPrompt)}
            >
              {loading ? <LoaderIcon size={16} className="mr-2 animate-spin" /> : <SaveIcon size={16} className="mr-2" />}
              Save Changes
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                className="h-10 text-[11px] font-semibold bg-white/5 border-stroke/20 text-muted hover:text-text hover:bg-white/10"
                onClick={handleReset}
                disabled={loading}
              >
                <RotateCcwIcon size={14} className="mr-1.5 opacity-60" />
                Reset
              </Button>
              <Button
                variant="secondary"
                className="h-10 text-[11px] font-semibold bg-white/5 border-stroke/20 text-muted hover:text-text hover:bg-white/10"
                onClick={handleCopy}
                disabled={loading}
              >
                {copied ? (
                  <CheckIcon size={14} className="mr-1.5 text-green" />
                ) : (
                  <CopyIcon size={14} className="mr-1.5 opacity-60" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:w-3/4 space-y-6">
          <div className="flex justify-between items-end pb-2 border-b border-stroke">
            <div>
              <h2 className="text-2xl font-bold text-text flex items-center gap-3">
                <Sparkles className="text-accent" size={24} />
                Editor
              </h2>
              <div className="text-xs text-muted font-medium mt-1">
                Refining {promptTypes.find(pt => pt.id === selectedPromptType)?.name || 'Unknown Type'}
              </div>
            </div>
            {saveStatus && (
              <Badge variant={saveStatus.includes('Failed') ? 'destructive' : 'default'} className="animate-in fade-in slide-in-from-right-2">
                {saveStatus}
              </Badge>
            )}
          </div>

          <div className="bg-panel rounded-2xl p-5 border border-stroke shadow-inner">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted/60 mb-4 flex items-center">
              <BrainIcon size={14} className="mr-2 text-accent" />
              Provider & Model
              {promptRequiresVision && (
                <Badge variant="secondary" className="ml-3 text-[9px] border-stroke">Vision Models Only</Badge>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Provider
                </label>
                <div className="relative" data-dropdown="provider-selector">
                  <button
                    className="w-full text-left p-2 bg-gray-700 text-gray-200 hover:bg-gray-600 rounded-lg flex items-center justify-between text-sm"
                    onClick={() => setIsProviderDropdownOpen(!isProviderDropdownOpen)}
                  >
                    <div className="flex items-center">
                      <ServerIcon size={14} className="mr-2" />
                      <span className="truncate">
                        {currentProvider?.name || 'Select a provider...'}
                      </span>
                    </div>
                    <ChevronDownIcon size={14} className={`ml-2 transition-transform ${isProviderDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isProviderDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg">
                      {availableProviders.length > 0 ? (
                        availableProviders.map(provider => (
                          <button
                            key={provider.id}
                            className={`w-full text-left px-3 py-2 hover:bg-gray-600 text-sm first:rounded-t-lg last:rounded-b-lg ${formData.provider === provider.id ? 'bg-indigo-600 text-white' : 'text-gray-200'}`}
                            onClick={() => handleProviderChange(provider.id as SupportedProvider)}
                          >
                            <div className="flex items-center">
                              <ServerIcon size={14} className="mr-2" />
                              <span className="truncate">{provider.name}</span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          No providers with vision-capable models available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Model
                  {modelsLoading && <LoaderIcon size={14} className="ml-2 animate-spin text-indigo-400" />}
                </label>
                <div className="relative" data-dropdown="model-selector">
                  <button
                    className="w-full text-left p-2 bg-gray-700 text-gray-200 hover:bg-gray-600 rounded-lg flex items-center justify-between text-sm"
                    onClick={() => {
                      const next = !isModelDropdownOpen;
                      setIsModelDropdownOpen(next);
                      if (!next) {
                        setModelSearchTerm('');
                      }
                    }}
                    disabled={modelsLoading || currentProvider.models.length === 0}
                  >
                    <div className="flex items-center">
                      <BrainIcon size={14} className="mr-2" />
                      <span className="truncate">
                        {currentProvider.models.find((m) => m.id === formData.model)?.name ||
                          (currentProvider.models.length > 0 ? 'Select a model...' : 'No models available')}
                      </span>
                    </div>
                    <ChevronDownIcon size={14} className={`ml-2 transition-transform ${isModelDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isModelDropdownOpen && currentProvider.models.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg">
                      <div className="px-3 pt-3 pb-2 border-b border-gray-600 bg-gray-700">
                        <div className="relative">
                          <SearchIcon size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            ref={modelSearchInputRef}
                            className="w-full pl-7 pr-2 py-1.5 bg-gray-600 text-gray-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="Search models..."
                            value={modelSearchTerm}
                            onChange={event => setModelSearchTerm(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {hasFilteredModels ? (
                          ['openrouter', 'nanogpt', 'ollama'].includes(currentProvider.id) ? (
                            (() => {
                              const groupedModels = filteredModels.reduce((groups: Record<string, ProviderModel[]>, model) => {
                                const providerName = typeof model?.id === 'string' ? model.id.split('/')[0] || 'Other' : 'Other';
                                if (!groups[providerName]) groups[providerName] = [];
                                groups[providerName].push(model);
                                return groups;
                              }, {} as Record<string, ProviderModel[]>);

                              return (Object.entries(groupedModels) as [string, ProviderModel[]][]).map(([providerName, models]) => (
                                <div key={providerName}>
                                  <div className="px-3 py-2 text-xs font-medium text-gray-400 bg-gray-600 border-b border-gray-500">
                                    {providerName.toUpperCase()}
                                  </div>
                                  {models.map((model) => (
                                    <button
                                      key={model.id}
                                      className={`w-full text-left px-3 py-2 hover:bg-gray-600 text-sm ${formData.model === model.id ? 'bg-indigo-600 text-white' : 'text-gray-200'}`}
                                      onClick={() => {
                                        handleFormDataChange('model', model.id);
                                        setIsModelDropdownOpen(false);
                                        setModelSearchTerm('');
                                      }}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="truncate">{model.name}</span>
                                        <div className="flex items-center gap-2">
                                          {modelSupportsVision(model) && (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-600/50 text-indigo-200">VISION</span>
                                          )}
                                          {formatModelPricing(model) && (
                                            <span className="text-xs text-gray-400">{formatModelPricing(model)}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">{model.id}</div>
                                    </button>
                                  ))}
                                </div>
                              ));
                            })()
                          ) : (
                            filteredModels.map((model) => (
                              <button
                                key={model.id}
                                className={`w-full text-left px-3 py-2 hover:bg-gray-600 text-sm ${formData.model === model.id ? 'bg-indigo-600 text-white' : 'text-gray-200'}`}
                                onClick={() => {
                                  handleFormDataChange('model', model.id);
                                  setIsModelDropdownOpen(false);
                                  setModelSearchTerm('');
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="truncate">{model.name}</span>
                                  <div className="flex items-center gap-2">
                                    {modelSupportsVision(model) && (
                                      <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-600/50 text-indigo-200">VISION</span>
                                    )}
                                    {formatModelPricing(model) && (
                                      <span className="text-xs text-gray-400">{formatModelPricing(model)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 truncate">{model.id}</div>
                              </button>
                            ))
                          )
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-300">
                            {promptRequiresVision
                              ? `No vision-capable models match "${modelSearchTerm.trim() || 'your search'}".`
                              : `No models match "${modelSearchTerm.trim()}".`
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {modelsError && (
                  <div className="mt-2 p-2 bg-red-900/20 border border-red-700 rounded-sm text-red-300 text-xs">
                    Failed to load models. <button onClick={() => refetchModels()} className="underline hover:no-underline">Retry</button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Model Provider
                  {(providersLoading || endpointsLoading) && <LoaderIcon size={14} className="ml-2 animate-spin text-indigo-400" />}
                </label>
                <div className="relative" data-dropdown="model-provider-selector">
                  <button
                    className="w-full text-left p-2 bg-gray-700 text-gray-200 hover:bg-gray-600 rounded-lg flex items-center justify-between text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      if (isModelProviderEnabled) {
                        setIsModelProviderDropdownOpen(!isModelProviderDropdownOpen);
                      }
                    }}
                    disabled={!isModelProviderEnabled || providersLoading}
                  >
                    <div className="flex items-center">
                      <ServerIcon size={14} className="mr-2" />
                      <span className="truncate">
                        {!isModelProviderEnabled ? 'OpenRouter only' :
                          formData.modelProvider ?
                            allModelProviders.find((p) => p.code === formData.modelProvider)?.name || formData.modelProvider :
                            'Auto (best uptime)'}
                      </span>
                    </div>
                    <ChevronDownIcon size={14} className={`ml-2 transition-transform ${isModelProviderDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isModelProviderDropdownOpen && isModelProviderEnabled && (
                    <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg">
                      <div className="px-3 pt-3 pb-2 border-b border-gray-600 bg-gray-700">
                        <div className="relative">
                          <SearchIcon size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            ref={modelProviderSearchInputRef}
                            type="text"
                            className="w-full pl-7 pr-2 py-1.5 bg-gray-600 text-gray-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="Search providers..."
                            value={modelProviderSearchTerm}
                            onChange={event => setModelProviderSearchTerm(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        <button
                          className={`w-full text-left px-3 py-2 hover:bg-gray-600 text-sm border-b border-gray-600 ${!formData.modelProvider ? 'bg-indigo-600 text-white' : 'text-gray-200'}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, modelProvider: undefined }));
                            setIsModelProviderDropdownOpen(false);
                            setModelProviderSearchTerm('');
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <ZapIcon size={14} className="mr-2" />
                              <span>Auto (best uptime)</span>
                            </div>
                            <span className="text-xs text-gray-400">Recommended</span>
                          </div>
                        </button>

                        {filteredModelProviders.length > 0 ? (
                          filteredModelProviders.map((provider) => {
                            const uptime = getProviderUptime(provider.code);
                            const uptimePercent = uptime ? uptime.toFixed(1) : null;
                            const isSelected = formData.modelProvider === provider.code;

                            return (
                              <button
                                key={provider.code}
                                className={`w-full text-left px-3 py-2 hover:bg-gray-600 text-sm ${isSelected ? 'bg-indigo-600 text-white' : 'text-gray-200'}`}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, modelProvider: provider.code }));
                                  setIsModelProviderDropdownOpen(false);
                                  setModelProviderSearchTerm('');
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <ServerIcon size={14} className="mr-2" />
                                    <span className="truncate">{provider.name}</span>
                                  </div>
                                  {uptimePercent && (
                                    <span className={`text-xs ${parseFloat(uptimePercent) >= 99 ? 'text-green-400' : parseFloat(uptimePercent) >= 95 ? 'text-yellow-400' : 'text-red-400'}`}>
                                      {uptimePercent}%
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-400">
                            {modelProviderSearchTerm.trim()
                              ? `No providers match "${modelProviderSearchTerm.trim()}".`
                              : 'No providers available for this model.'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {!isModelProviderEnabled && (
                  <div className="mt-2 text-xs text-gray-400">
                    Select OpenRouter as provider to choose model provider
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-panel rounded-2xl p-5 border border-stroke shadow-inner space-y-6">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted/60 flex items-center">
              <MessageSquareIcon size={14} className="mr-2 text-accent" />
              Prompts
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted/80 px-1 italic">
                  System Prompt
                </label>
                <textarea
                  ref={systemPromptRef}
                  value={formData.systemPrompt}
                  onChange={(e) => handleFormDataChange('systemPrompt', e.target.value)}
                  placeholder="The core persona and rules..."
                  rows={8}
                  className={clsx(
                    "w-full px-4 py-3 bg-panel-strong border rounded-2xl text-text placeholder-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/20 focus:border-accent/40 resize-y font-mono text-xs leading-relaxed transition-all",
                    showSystemHighlight ? "border-green ring-1 ring-green/20" : "border-stroke"
                  )}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <label className="text-xs font-bold text-muted/80 italic">
                    User Prompt Template
                  </label>
                  {variableHints && (
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircleIcon size={14} className="text-muted/40 hover:text-accent transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent side="left" className="w-[300px] text-[10px] leading-relaxed">
                        {variableTooltip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <textarea
                  ref={userPromptRef}
                  value={formData.userPrompt}
                  onChange={(e) => handleFormDataChange('userPrompt', e.target.value)}
                  placeholder="The template for dynamic interaction..."
                  rows={6}
                  className={clsx(
                    "w-full px-4 py-3 bg-panel-strong border rounded-2xl text-text placeholder-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/20 focus:border-accent/40 resize-y font-mono text-xs leading-relaxed transition-all",
                    showUserHighlight ? "border-green ring-1 ring-green/20" : "border-stroke"
                  )}
                />
                {variableHints && (
                  <div className="mt-3 bg-white/5 border border-stroke/40 rounded-xl p-3">
                    <p className="text-[10px] text-muted font-semibold uppercase tracking-widest mb-3 px-1">Dynamic Placeholders</p>
                    <div className="flex flex-wrap gap-2">
                      {currentVariableHints.map(hint => {
                        const isUsed = matchedVariables.has(hint.name);
                        return (
                          <button
                            type="button"
                            key={hint.name}
                            className={clsx(
                              "text-[10px] font-bold px-2.5 py-1.5 rounded-lg border transition-all duration-200",
                              isUsed
                                ? "border-green/40 bg-green/10 text-green shadow-[0_0_10px_rgba(var(--green-rgb),0.1)]"
                                : "border-stroke bg-panel text-muted/60 hover:text-text hover:border-muted/40"
                            )}
                            title={hint.description || undefined}
                            onClick={() => insertVariablePlaceholder(hint.name)}
                          >
                            {`{{${hint.name}}}`}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-panel rounded-2xl p-5 border border-stroke shadow-inner">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted/60 mb-5 flex items-center">
              <ZapIcon size={14} className="mr-2 text-accent" />
              Advanced Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted/80 px-1 italic">
                  Max Tokens
                </label>
                <Input
                  type="number"
                  className="bg-panel-strong border-stroke h-10 text-sm rounded-xl"
                  value={formData.maxTokens}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      handleFormDataChange('maxTokens', '');
                    } else {
                      const parsed = parseInt(value);
                      handleFormDataChange('maxTokens', isNaN(parsed) ? 200 : parsed);
                    }
                  }}
                  placeholder="2000"
                  min="50"
                  max="4000"
                  disabled={loading}
                />
                <p className="text-[10px] text-muted italic px-1 opacity-60">Control response length (50-4000)</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted/80 px-1 italic">
                  Temperature
                </label>
                <div className="pt-3 px-1">
                  <input
                    type="range"
                    className="w-full h-1.5 bg-stroke rounded-lg appearance-none cursor-pointer accent-accent"
                    value={formData.temperature}
                    onChange={(e) => handleFormDataChange('temperature', parseFloat(e.target.value))}
                    min="0"
                    max="2.0"
                    step="0.1"
                    disabled={loading}
                  />
                  <div className="flex justify-between text-[10px] font-bold uppercase tracking-tighter text-muted/40 mt-3">
                    <span>Focused</span>
                    <Badge variant="secondary" className="bg-accent/10 border-accent/20 text-accent">{formData.temperature}</Badge>
                    <span>Creative</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted/80 px-1 italic">
                  Prompt Cache
                </label>
                <Select
                  value={formData.promptCacheTtl}
                  onValueChange={(val) => handleFormDataChange('promptCacheTtl', val)}
                >
                  <SelectTrigger className="bg-panel-strong border-stroke h-10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Disabled</SelectItem>
                    <SelectItem value="5m">5 Minutes</SelectItem>
                    <SelectItem value="1h">1 Hour</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted italic px-1 opacity-60">Reduce costs for repeated context</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
